import React from 'react';
import Grid from '@mui/material/Grid2';

import { 
  CharacterAbility,
  CharacterSpell,
  Attack,
  CharacterItem,
  CharacterWeapon,
  ExtraEffectsFeature,
  CharacterAction,
  CharacterSheetInterface,
  Character,
  CreatureInstance,
  SummonInstance,
  CharacterExtraEffects,
  ExtraEffectsOfTypeFeature,
  StoredSpell
} from "@dndModels";
import { DAMAGE_TYPES } from "@dndModels/Constants";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharacterEffectPDF from "@dndComponents/character/pdf/CharacterEffectPDF";
import ImageFromString from "@dndComponents/display/ImageFromString";
import CharacterCastButtonPDF from "@dndComponents/character/pdf/CharacterCastButtonPDF";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import { isDamageType } from '@dndModels/Types';

interface Props {
  character?: Character;
  char_int: CharacterSheetInterface | CreatureInstance | SummonInstance;
  obj: CharacterAbility | CharacterSpell | StoredSpell | CharacterItem | Attack;
  action: CharacterAction;
  level: number;
  log_it?: boolean;
  show_extra_effects?: boolean;
  casting_time: string; // 'A' | 'BA' | 'RA' | 'Special' | 'Attack' | "1 Minute" | "10 Minutes" | "1 Hour";
}

const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterEffectsPDF({
  character,
  char_int,
  obj,
  action,
  log_it = false,
  level,
  show_extra_effects = false,
  casting_time,
}: Props) {
  if (log_it) {
    debugger;
  }

  const renderCost = (the_action: CharacterAbility | CharacterSpell | StoredSpell | CharacterItem | Attack | CharacterExtraEffects) => {
    const action_type = action.type;
    const action_level = action.level;
    if (the_action instanceof CharacterAbility) {
      return (
        <CharacterCastButtonPDF
          obj={the_action} 
          character={character}
          casting_time={action.ritual_only ? "Ritual" : action.casting_time}
          type={action_type}
          level={action_level}
        />
      );
    } else if (the_action instanceof CharacterSpell) {
      const spell = the_action;
      const the_level = level === -1 ? spell.level : level;
      let level2 = the_level;
      if (level2 > 0) {
        if (char_int instanceof CreatureInstance || char_int instanceof SummonInstance) {
          console.error("slots not implemented for creatures");
        } else {
          const slots = char_int.use_slots.filter(o => o.level === the_level);
          if (slots.length === 0) {
            level2 = 9; 
            char_int.use_slots.filter(o => o.level > the_level).forEach(s => {
              if (s.level < level2) {
                level2 = s.level;
              }
            });
          }
        }
      }
      return (
        <CharacterCastButtonPDF
          obj={the_action} 
          character={character}
          casting_time={action.ritual_only ? "Ritual" : action.casting_time}
          type={action_type}
          level={level2}
        />
      );
    } else if (the_action instanceof CharacterExtraEffects && the_action.time_restriction !== "Hide") {
      let time_restriction = the_action.time_restriction + (the_action.extra_restriction === "" ? "" : ` ${the_action.extra_restriction}`);
      if (time_restriction === "Every Time") {
        time_restriction = "";
      }
      const fontSize = time_restriction.length < 15 ? "10px" : "6px";
      return (
        <Grid container spacing={0} direction="row">
          { the_action.resource_consumed !== "None" &&
            <Grid
              size={{
                xs: 12
              }}>
              { the_action.amount_consumed }&nbsp;
              { the_action.resource_consumed === "Slot" ? 
                <>Level { the_action.slot_level } Slot</>
              : 
                <DisplayObjects type='resource' ids={[the_action.resource_consumed]} />
              }
            </Grid>
          }
          { time_restriction.length > 0 &&
            <Grid
              size={{
                xs: 12
              }}>
              <span style={{ fontSize }}>
                { time_restriction }
              </span>
            </Grid>
          }
        </Grid>
      );
    } else {
      return "";
    }
  }
  
  const renderContent = (name: string) => {
    const pieces = name.split("|");
    const return_me: any[] = [];
    for (let i = 0; i < pieces.length; ++i) {
      const piece = pieces[i];
      const strs = piece.split(/{|}|,/);
      for (const s of strs) {
        const s2 = s.trim();
        if (s2.length > 0) {
          if (isDamageType(s2)) {
            return_me.push(<ImageFromString key={return_me.length} image={s2} />);
          } else {
            return_me.push(<span key={return_me.length}>{s2}&nbsp;</span>);
          }
        }
      }
      if (i + 1 < pieces.length) {
        return_me.push(<br key={return_me.length} />);
      }
    }
    
    return return_me;
  }

  if (obj instanceof CharacterItem) { 
    if (obj instanceof CharacterWeapon) {
      const weapon = obj;
      const filtered = weapon.attacks.filter(o => (casting_time !== "BA" && !o.bonus_action) || (casting_time === "BA" && o.bonus_action));
      
      if (filtered.length > 0) {
        // const extra_effects = char_int instanceof CreatureInstance || char_int instanceof SummonInstance ? [] : (character ? char_util.getExtraEffects({ char_int, char: character, obj: weapon, action }) : []);
        // console.log(extra_effects);
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 3
              }}></Grid>
            <Grid
              container
              spacing={0}
              direction="row"
              size={{
                xs: 9
              }}>
              { filtered.map((attack, key) => {
                const range_string = char_int instanceof CreatureInstance || char_int instanceof SummonInstance ? "range_string not implemented" : char_util.getRangeString({ char_int: char_int, weapon, attack });
                const range_font_size = (range_string.length + attack.type.length) < 14 ? "10px" : "6px";
                return (
                  <Grid
                    key={key}
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 12
                    }}>
                    <Grid
                      className="centered"
                      style={{ fontSize: range_font_size }}
                      size={{
                        xs: 5
                      }}>
                      { attack.type }&nbsp;{ range_string }
                    </Grid>
                    <Grid
                      className="centered"
                      size={{
                        xs: 3
                      }}>
                      { attack.attack_string !== "No Attack" &&
                        <span>{ attack.attack_string }</span>
                      }
                    </Grid>
                    <Grid
                      className="centered"
                      size={{
                        xs: 4
                      }}>
                      { renderContent(attack.damage_string) }
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
            {/* { show_extra_effects && extra_effects.map((ee, key) => {
              return (
                <Grid item key={key} xs={12} container spacing={0} direction="row" style={{ border: "1px solid" }}>
                  <Grid item xs={3} className="centered">
                    { renderCost(ee) }
                  </Grid>
                  <Grid item xs={9} container spacing={0} direction="row">
                    { ee.effect_cfs.map((cf, key2) => {
                      return (
                        <Grid item key={key2} xs={12} container spacing={0} direction="row">
                          { (cf.floating_feature instanceof ExtraEffectsFeature || cf.floating_feature instanceof ExtraEffectsOfTypeFeature) && cf.floating_feature.effects.map((effect, key3) => {
                            return (
                              <Grid item xs={12} key={key3}>
                                <CharacterEffectPDF
                                  // spell={spell2}
                                  effect={effect}
                                  action={action}
                                  character={character}
                                  char_int={char_int}
                                  level={level}
                                  // wide={wide}
                                  // attack_mod={attack_mod}
                                  // casting_time={casting_time}
                                  // processing={processing}
                                  // disabled={!enabled}
                                  // shared={shared}
                                  // show_extra_effects={show_extra_effects}
                                  log_it={log_it}
                                  ee_cf={cf}
                                  // type_cf={ ee.main_cf.true_id === cf.true_id ? undefined : ee.main_cf }
                                  // onChange={onChange}
                                  // onCostChange={(costs: { [resource: string]: number }) => {
                                  //   setChangedCosts(costs);
                                  //   setReloadingCosts(true);
                                  // }}
                                  // onTransform={onTransform}
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              );
            })} */}
          </Grid>
        );
      } else return null;
    }
  } else if (obj instanceof Attack) {
    const attack = obj;
    const range_string = char_int instanceof CreatureInstance || char_int instanceof SummonInstance ? "range_string not implemented" : char_util.getRangeString({ char_int: char_int, attack });
    const range_font_size = range_string.length < 15 ? "10px" : "6px";
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 3
          }}></Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 9
          }}>
          <Grid
            size={{
              xs: 5
            }}>
            <Grid container spacing={0} direction="column">
              <Grid className="centered">
                { attack.type }
              </Grid>
              <Grid className="centered" style={{ fontSize: range_font_size }}>
                { range_string }
              </Grid>
            </Grid>
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 3
            }}>
            { attack.attack_string }
          </Grid>
          <Grid
            className="centered"
            size={{
              xs: 4
            }}>
            { renderContent(attack.damage_string) }
          </Grid>
        </Grid>
      </Grid>
    );
    // return (
    //   <Grid container spacing={0} direction="row">
    //     <Grid item xs={5} className="centered">
    //       { attack.type }&nbsp;{ range_string }
    //     </Grid>
    //     <Grid item xs={3} className="centered">
    //       { attack.attack_string }
    //     </Grid>
    //     <Grid item xs={4} className="centered">
    //       { renderContent(attack.damage_string) }
    //     </Grid>
    //   </Grid>
    // );
  } else {
    const spell2 = obj;
    let extra_effects = char_int instanceof CreatureInstance || char_int instanceof SummonInstance ? [] : (character ? char_util.getExtraEffects({ char_int, char: character, obj: spell2, action }) : []);
    extra_effects = extra_effects.filter(o => o.apply_to.length === 1 && o.apply_to.includes("Abilities") && o.ability_limit.name.length > 0);
    // console.log(obj);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          <Grid
            className="centered"
            size={{
              xs: 3
            }}>
            { renderCost(obj) }
          </Grid>
          <Grid
            container
            spacing={0}
            direction="row"
            size={{
              xs: 9
            }}>
            { obj.effects.map((effect, key) => {
              // debugger;
              if (!show_extra_effects && key > 0) {
                return null;
              }
              return (
                <Grid
                  key={key}
                  size={{
                    xs: 12
                  }}>
                  <CharacterEffectPDF
                    spell={spell2}
                    effect={effect}
                    action={action}
                    character={character}
                    char_int={char_int}
                    level={level}
                    log_it={log_it}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 12
          }}>
          { extra_effects.map((ee, key) => {
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                style={{ border: "1px solid" }}
                size={{
                  xs: 12
                }}>
                <Grid
                  className="centered"
                  size={{
                    xs: 3
                  }}>
                  { renderCost(ee) }
                </Grid>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 9
                  }}>
                  { ee.effect_cfs.map((cf, key2) => {
                    return (
                      <Grid
                        key={key2}
                        container
                        spacing={0}
                        direction="row"
                        size={{
                          xs: 12
                        }}>
                        { (cf.floating_feature instanceof ExtraEffectsFeature || cf.floating_feature instanceof ExtraEffectsOfTypeFeature) && cf.floating_feature.effects.map((effect, key3) => {
                          return (
                            <Grid
                              key={key3}
                              size={{
                                xs: 12
                              }}>
                              <CharacterEffectPDF
                                // spell={spell2}
                                effect={effect}
                                action={action}
                                character={character}
                                char_int={char_int}
                                level={level}
                                // wide={wide}
                                // attack_mod={attack_mod}
                                // casting_time={casting_time}
                                // processing={processing}
                                // disabled={!enabled}
                                // shared={shared}
                                // show_extra_effects={show_extra_effects}
                                log_it={log_it}
                                ee_cf={cf}
                                // type_cf={ ee.main_cf.true_id === cf.true_id ? undefined : ee.main_cf }
                                // onChange={onChange}
                                // onCostChange={(costs: { [resource: string]: number }) => {
                                //   setChangedCosts(costs);
                                //   setReloadingCosts(true);
                                // }}
                                // onTransform={onTransform}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  } 
  return <></>;
}

